import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  ButtonIcon,
  CardBase,
  CardBaseContent,
  CardBaseHeader,
  Chips,
  FlexContainer,
  Label,
  LabelBase,
  Map,
  OptionMenu,
  OptionMenuItem,
  Spinner,
  Status,
  StatusBase,
  StatusType,
  TableLegacy,
  useTimezone,
} from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { fakeInteractions } from '../../api/fakeData';
import { useApi } from '../../hooks';
import { useArrayNavigation } from '../../hooks/useArrayNavigation';
import { useEmptyState } from '../../hooks/useEmptyState';
import { usePagination } from '../../hooks/usePagination';
import { useCiam } from '../../providers';
import { MAX_ITEMS_PAGE, OPERATIONS_COLORS } from '../../state/constants';
import {
  Assetstype,
  UserPersonalInformation,
  UsersDetailOperations,
} from '../../state/model/user';
import {
  getUserDetail,
  searchUsersDetailOperations,
} from '../../state/queries/users';
import { EmptyData } from '../emptyData';
import { ModalImageDetail } from '../modalImageDetail';
import {
  StyledCardOverflow,
  StyledCustomTabs,
  StyledGridCustomer,
  StyledHeader,
  StyledListElement,
  StyledTableGrid,
} from './Styles';

export const CustomerTab = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { getDocumentImage } = useApi();
  const { onUserOperation } = useCiam();
  const { formatTimezone } = useTimezone();
  const [files, setFiles] = useState<Assetstype[]>([]);
  const [position, setPotision, { prev, next, itemSelected }] =
    useArrayNavigation<Assetstype>(files);

  const {
    data: { searchUserDetailOperations } = {},
    loading: loadingOperations,
    fetchMore,
  } = useQuery<{
    searchUserDetailOperations: UsersDetailOperations;
  }>(searchUsersDetailOperations, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      userId: id,
      parameters: {
        limit: MAX_ITEMS_PAGE,
      },
    },
  });

  const { data: { getUserDetails } = {}, loading: loadingUser } = useQuery<{
    getUserDetails: UserPersonalInformation;
  }>(getUserDetail, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      userId: id,
    },
  });

  const { handlePagination } = usePagination(
    loadingOperations,
    fetchMore,
    searchUserDetailOperations?.pageInfo.hasNextPage,
    searchUserDetailOperations?.pageInfo.endCursor
  );

  const columns = useMemo(
    () => [
      {
        Header: t('Date'),
        accessor: 'date',
        maxWidth: 200,
      },
      {
        Header: t('Operations'),
        accessor: 'operations',
      },
      {
        Header: t('Assets'),
        accessor: 'assets',
      },
      {
        Header: t('Status'),
        accessor: 'status',
      },
      {
        Header: t('Action'),
        accessor: 'action',
        maxWidth: 100,
      },
    ],
    []
  );

  const dataDetailsOperations = searchUserDetailOperations?.edges?.map(
    (item) => ({
      date: formatTimezone(item.node.creationTime, 'DD/MM/YYYY HH:mm:ss'),
      operations: (
        <LabelBase
          color={OPERATIONS_COLORS[item.node.operationType.toLowerCase()]}
          label={t(item.node.operationType.toLowerCase())}
        />
      ),
      assets: item.node.assets?.length > 0 && (
        <OptionMenu
          renderItem={
            <span>
              <Chips label={`${t('Files')} (${item.node.assets.length})`} />
            </span>
          }
        >
          {item.node.assets.map((file, index) => (
            <OptionMenuItem
              key={file.url}
              iconName="FileArrowDown"
              onClick={() => {
                setFiles(item.node.assets);
                setPotision(index);
                getDocumentImage(file.url);
              }}
            >
              {t(file.type)}
            </OptionMenuItem>
          ))}
        </OptionMenu>
      ),
      status: (
        <Status
          status={item.node.results.at(-1)?.status as StatusType}
          hasLabel
          locale={i18n.language}
        />
      ),
      action: (
        <ButtonIcon
          iconName="Eye"
          variant="text"
          onClick={() => onUserOperation?.(item.node.operationId)}
        />
      ),
    })
  );

  const { emptyState } = useEmptyState({
    data: dataDetailsOperations || [],
    isLoading: loadingOperations,
  });

  return (
    <StyledCustomTabs flexDirection="column" rowGap="1.6">
      {/* Header */}
      <StyledHeader forwardedAs="section" columnGap="8.8">
        <FlexContainer columnGap="0.4">
          <Label semibold fontSize="12">
            {t('Sign-up date')}:
          </Label>
          <Label fontSize="12">20/05/21 17:28:32</Label>
        </FlexContainer>
        <FlexContainer columnGap="0.4">
          <Label semibold fontSize="12">
            {t('User Id')}:
          </Label>
          <Label fontSize="12">608d74531e8f6</Label>
        </FlexContainer>
        <StatusBase color="green400" label={t('connected') as string} />
      </StyledHeader>
      <StyledGridCustomer
        gridTemplateColumns="repeat(3, 1fr)"
        gridTemplateRows="1fr 34rem"
        columnGap="2"
        rowGap="1.6"
        forwardedAs="section"
      >
        {/* First OCR received - User information */}
        <CardBase>
          <CardBaseHeader>
            <Label semibold>{t('First OCR received - User information')}</Label>
          </CardBaseHeader>
          <StyledCardOverflow flex={loadingUser ? '1' : '1 1 0px'}>
            {loadingUser ? (
              <Spinner insideComponent />
            ) : getUserDetails?.personalInformation ? (
              <FlexContainer as="ul" flexDirection="column" flex="1">
                {Object.entries(getUserDetails?.personalInformation).map(
                  ([key, value]) => (
                    <StyledListElement
                      forwardedAs="li"
                      justifyContent="space-between"
                      alignItems="center"
                      key={key}
                    >
                      <Label fontSize="12">{t(key)}</Label>
                      <Label semibold textAlign="right">
                        {value}
                      </Label>
                    </StyledListElement>
                  )
                )}
              </FlexContainer>
            ) : (
              <EmptyData />
            )}
          </StyledCardOverflow>
        </CardBase>
        {/* Last interactions */}
        <CardBase>
          <CardBaseHeader>
            <Label semibold>{t('Last interactions')}</Label>
          </CardBaseHeader>
          <StyledCardOverflow flex={loadingUser ? '1' : '1 1 0px'}>
            <FlexContainer as="ul" flexDirection="column" flex="1 1 0px">
              {fakeInteractions.map((item) => {
                return Object.entries(item).map(([key, value]) => (
                  <StyledListElement
                    forwardedAs="li"
                    key={value}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Label fontSize="12">{t(key)}</Label>
                    <Label semibold textAlign="right">
                      {value}
                    </Label>
                  </StyledListElement>
                ));
              })}
            </FlexContainer>
          </StyledCardOverflow>
        </CardBase>
        {/* Latest authentications geolocation */}
        <CardBase>
          <CardBaseHeader>
            <Label semibold>{t('Latest authentications geolocation')}</Label>
          </CardBaseHeader>
          <CardBaseContent
            flexDirection="column"
            justifyContent="space-between"
            flex="1"
          >
            {loadingUser ? (
              <Spinner insideComponent />
            ) : getUserDetails?.coordinates.length ? (
              <>
                <Map
                  markers={getUserDetails?.coordinates.map((data) => ({
                    lat: data.latitude,
                    lng: data.longitude,
                    name: '',
                  }))}
                />
                <Label fontSize="10">
                  {t('Latitude')}: {getUserDetails?.coordinates[0].latitude},{' '}
                  {t('Longitude')}: {getUserDetails?.coordinates[0].longitude}
                </Label>
              </>
            ) : (
              <EmptyData />
            )}
          </CardBaseContent>
        </CardBase>
        {/* Table */}
        <StyledTableGrid forwardedAs="section">
          <TableLegacy
            columns={columns}
            data={dataDetailsOperations || []}
            hasMore={false}
            emptyStateData={emptyState}
            fetchMore={handlePagination}
            loading={loadingOperations}
          />

          <ModalImageDetail
            onChangeShow={(show) => !show && setFiles([])}
            show={Boolean(itemSelected)}
            title={`${t(`${itemSelected?.type}`)} (${
              position !== null ? `${position + 1}/${files.length}` : ''
            })`}
            image={itemSelected?.url as string}
            onGoRight={next}
            onGoLeft={prev}
            disabledGoLeft={position === 0}
            disabledGoRight={position === files.length - 1}
          />
        </StyledTableGrid>
      </StyledGridCustomer>
    </StyledCustomTabs>
  );
};
